<script>
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import { uploadDocument } from '../../../../api/coLiving/documents';
import { apiErrorMessage } from '../../../../api/coLiving/errors';
import {
  BOT_DEFAULT_BY_TYPE,
  DOC_ACCEPT,
  documentFileProblem,
  formatBytes,
} from '../documents';

// "Upload Files": one or more files for one tab. The API takes one file per
// request and keeps nothing on a failure, so each file is sent on its own and
// gets its own result (a plan with no credits fails only the files that need
// them).
export default {
  name: 'DocumentUploadModal',
  components: { BaseModal },
  props: {
    condoId: { type: String, required: true },
    // API type: notice | mom | house_rules.
    type: { type: String, required: true },
  },
  emits: ['close', 'uploaded'],
  data() {
    return {
      accept: DOC_ACCEPT,
      files: [],
      botVisible: BOT_DEFAULT_BY_TYPE[this.type] !== false,
      isUploading: false,
      // { name, ok, message } per file, in the order sent.
      results: [],
    };
  },
  computed: {
    hasValidFile() {
      return this.files.some(item => !item.problem);
    },
    isDone() {
      return this.results.length > 0 && !this.isUploading;
    },
  },
  methods: {
    formatBytes,
    onPick(event) {
      this.results = [];
      this.files = Array.from(event.target.files || []).map(file => ({
        file,
        problem: documentFileProblem(file),
      }));
    },
    async onSubmit() {
      this.isUploading = true;
      this.results = [];
      let anyOk = false;
      // Sequential: the knowledge service charges per file, and a refusal
      // (no credits) should be seen on the first one it affects.
      await this.files
        .filter(item => !item.problem)
        .reduce(async (previous, item) => {
          await previous;
          const form = new FormData();
          form.append('file', item.file);
          form.append('type', this.type);
          form.append('bot_visible', this.botVisible ? 'true' : 'false');
          try {
            await uploadDocument(this.condoId, form);
            anyOk = true;
            this.results.push({ name: item.file.name, ok: true, message: '' });
          } catch (err) {
            this.results.push({
              name: item.file.name,
              ok: false,
              message: apiErrorMessage(
                this,
                err,
                'CO_LIVING.CONDOS.DOCUMENTS.UPLOAD_FAILED'
              ),
            });
          }
        }, Promise.resolve());
      this.isUploading = false;
      if (anyOk) this.$emit('uploaded', this.results);
    },
  },
};
</script>

<template>
  <BaseModal @close="$emit('close')">
    <template #title>
      {{ $t('CO_LIVING.CONDOS.DOCUMENTS.UPLOAD_TITLE') }}
    </template>

    <form
      class="grid grid-cols-1 gap-4"
      data-testid="co-living-document-upload-form"
      @submit.prevent="onSubmit"
    >
      <div>
        <label
          for="co-living-document-file"
          class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
        >
          {{ $t('CO_LIVING.CONDOS.DOCUMENTS.CHOOSE_FILES') }}
        </label>
        <input
          id="co-living-document-file"
          type="file"
          multiple
          :accept="accept"
          class="block w-full text-sm text-slate-700 dark:text-slate-200"
          data-testid="co-living-document-file"
          @change="onPick"
        />
        <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{ $t('CO_LIVING.CONDOS.DOCUMENTS.FILE_HINT') }}
        </p>
      </div>

      <ul
        v-if="files.length && !results.length"
        class="m-0 text-sm list-none"
        data-testid="co-living-document-picked"
      >
        <li
          v-for="item in files"
          :key="item.file.name"
          class="flex justify-between gap-3 py-1"
        >
          <span class="truncate text-slate-900 dark:text-slate-100">
            {{ item.file.name }}
            <span class="text-xs text-slate-500">
              {{ formatBytes(item.file.size) }}
            </span>
          </span>
          <span
            v-if="item.problem"
            class="text-xs text-red-500"
            :data-testid="`co-living-document-problem-${item.file.name}`"
          >
            {{ $t(`CO_LIVING.CONDOS.DOCUMENTS.FILE_ERRORS.${item.problem}`) }}
          </span>
        </li>
      </ul>

      <label class="flex items-start gap-2 text-sm">
        <input
          v-model="botVisible"
          type="checkbox"
          class="mt-1"
          data-testid="co-living-document-bot-visible"
        />
        <span class="text-slate-700 dark:text-slate-200">
          {{ $t('CO_LIVING.CONDOS.DOCUMENTS.BOT_VISIBLE') }}
          <span class="block text-xs text-slate-500 dark:text-slate-400">
            {{ $t('CO_LIVING.CONDOS.DOCUMENTS.BOT_VISIBLE_HINT') }}
          </span>
        </span>
      </label>

      <ul
        v-if="results.length"
        class="m-0 text-sm list-none"
        data-testid="co-living-document-results"
      >
        <li
          v-for="result in results"
          :key="result.name"
          class="py-1"
          :data-testid="`co-living-document-result-${result.name}`"
        >
          <span
            :class="
              result.ok
                ? 'text-green-700 dark:text-green-300'
                : 'text-red-600 dark:text-red-400'
            "
          >
            {{
              result.ok
                ? $t('CO_LIVING.CONDOS.DOCUMENTS.UPLOADED')
                : $t('CO_LIVING.CONDOS.DOCUMENTS.NOT_UPLOADED')
            }}
          </span>
          {{ result.name }}
          <span v-if="result.message" class="block text-xs text-red-500">
            {{ result.message }}
          </span>
        </li>
      </ul>

      <div class="flex justify-end gap-3">
        <woot-button variant="clear" type="button" @click="$emit('close')">
          {{
            isDone
              ? $t('CO_LIVING.CONDOS.DOCUMENTS.DONE')
              : $t('CO_LIVING.COMMON.CANCEL')
          }}
        </woot-button>
        <woot-button
          type="submit"
          :is-loading="isUploading"
          :is-disabled="!hasValidFile || isUploading || isDone"
          data-testid="co-living-document-upload-submit"
        >
          {{ $t('CO_LIVING.CONDOS.DOCUMENTS.UPLOAD') }}
        </woot-button>
      </div>
    </form>
  </BaseModal>
</template>
